import { useSyncExternalStore, useEffect, useCallback } from "react";
import { getContracts, getProvider } from "@/lib/contracts";
import { createStore } from "@/lib/externalStore";

/**
 * 当前账户的 $CATCLAW 余额，共享 store + 手动刷新，不做定时轮询。
 *
 * ActivatePanel 与 FusePanel 同屏时共用一份数据和一次请求。
 * 读链统一走项目 RPC，避免钱包 RPC 指向错误网络导致余额为 0。
 */
const store = createStore<bigint>(0n);

/** decimals 是 ERC20 常量，全局读一次即可 */
const decimalsStore = createStore<number>(18);
let decimalsLoaded = false;

let currentAccount: string | null = null;
let inflight: Promise<void> | null = null;

export async function refreshCatclawBalance(): Promise<void> {
  const account = currentAccount;
  if (!account) {
    store.set(0n);
    return;
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const { catclaw } = getContracts(getProvider());
      const [balance, decimals] = await Promise.all([
        catclaw.balanceOf(account),
        decimalsLoaded ? Promise.resolve(decimalsStore.get()) : catclaw.decimals().then((d: bigint) => Number(d)),
      ]);
      if (!decimalsLoaded) {
        decimalsLoaded = true;
        decimalsStore.set(decimals as number);
      }
      store.set(balance as bigint);
    } catch (err) {
      console.error("[useCatclawBalance] 读取余额失败", err);
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function useCatclawBalance(account?: string | null) {
  const balance = useSyncExternalStore(store.subscribe, store.get);
  const decimals = useSyncExternalStore(decimalsStore.subscribe, decimalsStore.get);

  // 仅在账户变化时拉取一次；后续由交易完成触发
  useEffect(() => {
    const next = account ?? null;
    if (next !== currentAccount) {
      currentAccount = next;
      refreshCatclawBalance();
    }
  }, [account]);

  const refresh = useCallback(() => refreshCatclawBalance(), []);

  return { balance, decimals, refresh };
}
